import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from 'src/app/services/toast.service';
import { SocialFeedService } from '../../services/social-feed.service';
import { SocialHubService } from '../../services/social-hub.service';
import { SocialPost } from '../../models/social-feed.model';
import { PostAttachmentPicker } from '../../utils/post-attachment';
import { PostPollDraft } from '../../utils/post-poll-draft';

/** Where a new post goes: one fixed place, or (no target) the "Post to" picker. */
export interface ComposerTarget {
  group?: number;
  gameGroup?: number;
  community?: number;
}

/**
 * Writing a post, the same everywhere: text, an optional label, photos or a
 * video, emoji, a poll. With a `target` the post goes there (a group's or
 * community's own page); without one a "Post to" picker offers your feed,
 * your map groups, games and communities.
 */
@Component({
  selector: 'app-social-composer',
  templateUrl: './social-composer.component.html',
  styleUrls: ['./social-composer.component.scss'],
  standalone: false,
})
export class SocialComposerComponent implements OnDestroy {
  @Input() target: ComposerTarget | null = null;
  @Input() placeholder = 'Share something…';
  @Output() posted = new EventEmitter<SocialPost>();

  text = '';
  label = '';
  showLabel = false;
  picked = 'personal';
  posting = false;
  focused = false;
  attachment = new PostAttachmentPicker();
  poll = new PostPollDraft();

  constructor(
    private social: SocialFeedService,
    public hub: SocialHubService,
    private toaster: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnDestroy(): void {
    this.attachment.clear();
  }

  get expanded(): boolean {
    return this.focused || !!this.text || !!this.attachment.file || this.poll.open || this.showLabel;
  }

  get targetOptions(): { value: string; label: string; group: string }[] {
    const s = this.hub.snapshot;
    return [
      { value: 'personal', label: 'Your followers (public)', group: 'Your feed' },
      ...s.communities.map((c) => ({ value: `community:${c.id}`, label: c.name, group: 'Communities' })),
      ...s.groups.map((g) => ({ value: `map:${g.id}`, label: g.name, group: 'Map groups' })),
      ...s.gameGroups.map((g) => ({ value: `game:${g.id}`, label: g.name, group: 'Games' })),
    ];
  }

  get canPost(): boolean {
    const hasText = !!this.text.trim();
    if (this.posting || (!hasText && !this.attachment.file)) return false;
    return !this.poll.open || (hasText && this.poll.valid);
  }

  get initials(): string {
    const name = this.hub.snapshot.profile?.author.name || '';
    return name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  }

  get avatar(): string | null {
    const path = this.hub.snapshot.profile?.author.avatar;
    return path ? this.social.resolveImageUrl(path) : null;
  }

  onFile(event: Event): void {
    this.attachment.onFileSelected(event);
  }

  private destination(): ComposerTarget {
    if (this.target) return this.target;
    const [kind, id] = this.picked.split(':');
    if (kind === 'community') return { community: Number(id) };
    if (kind === 'map') return { group: Number(id) };
    if (kind === 'game') return { gameGroup: Number(id) };
    return {};
  }

  publish(): void {
    if (!this.canPost) return;
    this.posting = true;
    this.social
      .createPost({
        content: this.text.trim(),
        activityLabel: this.label.trim() || undefined,
        images: this.attachment.images.length ? this.attachment.images : undefined,
        video: this.attachment.video || undefined,
        poll: this.poll.payload || undefined,
        ...this.destination(),
      })
      .subscribe({
        next: (post) => {
          this.posting = false;
          this.text = '';
          this.label = '';
          this.showLabel = false;
          this.focused = false;
          this.attachment.clear();
          this.poll.reset();
          this.posted.emit(post);
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.posting = false;
          const body = err?.error;
          const msg =
            (body && (body.error || body.detail || Object.values(body).flat().find((v) => typeof v === 'string'))) ||
            "Couldn't post. Please try again.";
          this.toaster.showNote(String(msg), 'danger');
          this.cdr.markForCheck();
        },
      });
  }
}
